import type { Airport } from '@trips/refdata';
import { newId } from '@trips/shared/ids';
import type { Place } from '@trips/shared/schema';
import { useId, useMemo, useState } from 'react';
import { put } from '../../data/mutate';
import { airportCity, searchAirports, useAirports } from '../../data/refdata';
import { ensureAirportPlace } from '../../data/trips';
import { api, isOffline } from '../../lib/api';
import { deviceTimeZone } from '../../lib/platform';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import s from './Pickers.module.css';

/** Airport by code, city or name, from the bundled dataset. Works offline. */
export function AirportField({
  label,
  value,
  onChange,
  placeholder = 'Airport code or city',
}: {
  label: string;
  value: Place | null;
  onChange: (p: Place | null) => void;
  placeholder?: string;
}) {
  const id = useId();
  const airports = useAirports();
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(!value);
  const results = useMemo(
    () => (airports && query ? searchAirports(airports, query) : []),
    [airports, query],
  );

  const pick = async (a: Airport) => {
    const p = await ensureAirportPlace(a);
    onChange(p);
    setQuery('');
    setEditing(false);
  };

  if (value && !editing) {
    return (
      <div className={s.field}>
        <span className={`${s.label} t-footnote`}>{label}</span>
        <button type="button" className={s.chosen} onClick={() => setEditing(true)}>
          <span className={`${s.code} wide`}>{value.iata}</span>
          <span className={s.chosenText}>
            <span className={s.chosenTitle}>{value.city}</span>
            <span className="t-footnote muted truncate">{value.name}</span>
          </span>
          <Icon name="pencil-simple" size={16} className={s.muted} />
        </button>
      </div>
    );
  }

  return (
    <div className={s.field}>
      <label htmlFor={id} className={`${s.label} t-footnote`}>
        {label}
      </label>
      <div className={s.control}>
        <Icon name="magnifying-glass" size={18} className={s.muted} />
        <input
          id={id}
          className={s.input}
          value={query}
          autoComplete="off"
          autoCapitalize="characters"
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results[0]) {
              e.preventDefault();
              void pick(results[0]);
            }
          }}
        />
      </div>
      {results.length > 0 && (
        <ul className={s.results}>
          {results.map((a) => (
            <li key={a.iata}>
              <button type="button" className={s.result} onClick={() => void pick(a)}>
                <span className={`${s.code} wide`}>{a.iata}</span>
                <span className={s.chosenText}>
                  <span className={s.chosenTitle}>{airportCity(a)}</span>
                  <span className="t-footnote muted truncate">{a.name}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!airports && <p className="t-footnote muted">Loading airports</p>}
    </div>
  );
}

interface GeocodeResult {
  name: string;
  address: string;
  lat: number;
  lon: number;
  tz: string | null;
  city: string | null;
  region: string | null;
  regionCode: string | null;
  country: string | null;
  countryCode: string | null;
}

/**
 * Address or venue. Search runs only when asked (the geocoder doesn't allow search-as-you-
 * type), and anything can be saved as typed and located later.
 */
export function PlaceField({
  label,
  value,
  onChange,
  kind,
  placeholder,
  near,
}: {
  label: string;
  value: Place | null;
  onChange: (p: Place | null) => void;
  kind: Place['kind'];
  placeholder?: string;
  /** Added to the search to favor results near the trip. */
  near?: string | null;
}) {
  const id = useId();
  const [query, setQuery] = useState(value ? (value.address ?? value.name) : '');
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!value);

  const search = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const q =
        near && !query.toLowerCase().includes(near.toLowerCase()) ? `${query}, ${near}` : query;
      const res = await api<{ results: GeocodeResult[] }>(
        `/api/geocode/search?q=${encodeURIComponent(q)}`,
      );
      setResults(res.results);
      if (res.results.length === 0)
        setError('No matches. Try adding the city, or save it as typed.');
    } catch (err) {
      setError(
        isOffline(err)
          ? "Search needs a connection. Save it as typed and it'll be located later."
          : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  const choose = async (r: GeocodeResult | null) => {
    const base = {
      id: value?.id ?? newId('plc'),
      kind,
      iata: null,
    };
    const place = r
      ? await put('places', {
          ...base,
          name: r.name,
          address: r.address,
          lat: r.lat,
          lon: r.lon,
          tz: r.tz,
          city: r.city,
          region: r.region,
          regionCode: r.regionCode,
          country: r.country,
          countryCode: r.countryCode,
          geo: 'geocoder' as const,
        })
      : await put('places', {
          ...base,
          name: query.trim(),
          address: query.trim(),
          lat: null,
          lon: null,
          tz: deviceTimeZone(),
          city: near ?? null,
          region: null,
          regionCode: null,
          country: null,
          countryCode: null,
          geo: 'none' as const,
        });
    onChange(place);
    setResults(null);
    setEditing(false);
  };

  if (value && !editing) {
    return (
      <div className={s.field}>
        <span className={`${s.label} t-footnote`}>{label}</span>
        <button type="button" className={s.chosen} onClick={() => setEditing(true)}>
          <Icon name="map-pin" size={20} className={s.muted} />
          <span className={s.chosenText}>
            <span className={s.chosenTitle}>{value.name}</span>
            {value.address && value.address !== value.name && (
              <span className="t-footnote muted truncate">{value.address}</span>
            )}
            {value.geo === 'none' && (
              <span className="t-footnote muted">Not located on the map yet</span>
            )}
          </span>
          <Icon name="pencil-simple" size={16} className={s.muted} />
        </button>
      </div>
    );
  }

  return (
    <div className={s.field}>
      <label htmlFor={id} className={`${s.label} t-footnote`}>
        {label}
      </label>
      <div className={s.control}>
        <input
          id={id}
          className={s.input}
          value={query}
          placeholder={placeholder ?? 'Name or address'}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void search();
            }
          }}
        />
        <Button
          size="sm"
          variant="quiet"
          onClick={() => void search()}
          disabled={busy || !query.trim()}
        >
          {busy ? 'Searching' : 'Find'}
        </Button>
      </div>
      {results && results.length > 0 && (
        <ul className={s.results}>
          {results.map((r, i) => (
            <li key={`${r.lat},${r.lon},${i}`}>
              <button type="button" className={s.result} onClick={() => void choose(r)}>
                <Icon name="map-pin" size={18} className={s.muted} />
                <span className={s.chosenText}>
                  <span className={s.chosenTitle}>{r.name}</span>
                  <span className="t-footnote muted truncate">{r.address}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="t-footnote muted">{error}</p>}
      {query.trim() && (
        <button type="button" className={s.asTyped} onClick={() => void choose(null)}>
          Use "{query.trim()}" as typed
        </button>
      )}
    </div>
  );
}
